<template>
  <div
    class="combobox"
    :class="{ combobox_desabilitado: desabilitado, combobox_aberto: aberto }"
  >
    <div class="combobox__input" @mousedown="aoPressionarNaCaixa">
      <i
        v-if="pesquisavel"
        class="far fa-search combobox__icone-busca"
        aria-hidden="true"
      ></i>
      <input
        v-bind="$attrs"
        ref="campo"
        type="text"
        class="combobox__campo"
        role="combobox"
        autocomplete="off"
        :readonly="!pesquisavel"
        :disabled="desabilitado"
        :aria-expanded="aberto ? 'true' : 'false'"
        aria-haspopup="listbox"
        :aria-controls="idLista"
        :aria-activedescendant="idOpcaoEmFoco"
        :value="valorExibidoNoCampo"
        @focus="abrir"
        @input="aoDigitar"
        @keydown="tratarTeclado"
      />
      <i
        class="far combobox__icone-seta"
        :class="aberto ? 'fa-chevron-up' : 'fa-chevron-down'"
        aria-hidden="true"
      ></i>
    </div>

    <ul
      v-if="aberto"
      :id="idLista"
      class="combobox__dropdown"
      role="listbox"
      :aria-multiselectable="multiplo ? 'true' : 'false'"
    >
      <li v-if="semResultados" class="combobox__sem-resultados">
        Nenhum resultado encontrado
      </li>
      <li
        v-for="(opcao, indice) in opcoesExibidas"
        v-else
        :id="idDaOpcao(indice)"
        :key="opcao.semSelecao ? '__sem-selecao__' : opcao.valor"
        role="option"
        class="combobox__opcao"
        :class="{
          combobox__opcao_foco: indice === indiceEmFoco,
          combobox__opcao_selecionada: opcaoEstaSelecionada(opcao),
        }"
        :aria-selected="opcaoEstaSelecionada(opcao) ? 'true' : 'false'"
        @click="selecionarOpcao(opcao)"
      >
        <input
          v-if="multiplo"
          type="checkbox"
          tabindex="-1"
          class="combobox__opcao-checkbox"
          :checked="opcaoEstaSelecionada(opcao)"
        />
        <slot name="opcao" :opcao="opcao">
          <span class="combobox__opcao-texto">{{ opcao.texto }}</span>
        </slot>
      </li>
    </ul>
  </div>
</template>

<script>
let proximoIdDeInstancia = 0;

export default {
  name: "combobox",
  inheritAttrs: false,
  props: {
    opcoes: { type: Array, required: true },
    value: { type: [String, Number, Array], default: null },
    pesquisavel: { type: Boolean, default: false },
    multiplo: { type: Boolean, default: false },
    preservarOrdem: { type: Boolean, default: false },
    rotuloSemSelecao: { type: String, required: true },
    rotuloDeSelecionados: { type: Function, default: null },
    desabilitado: { type: Boolean, default: false },
  },
  data() {
    proximoIdDeInstancia += 1;

    return {
      idUnico: `combobox-${proximoIdDeInstancia}`,
      aberto: false,
      termoPesquisa: "",
      indiceEmFoco: -1,
    };
  },
  computed: {
    idLista() {
      return `${this.idUnico}-lista`;
    },
    idOpcaoEmFoco() {
      return this.aberto && this.indiceEmFoco >= 0
        ? this.idDaOpcao(this.indiceEmFoco)
        : null;
    },
    opcaoDeAusenciaDeFiltro() {
      return { valor: null, texto: this.rotuloSemSelecao, semSelecao: true };
    },
    opcoesOrdenadas() {
      if (this.preservarOrdem) return [...this.opcoes];

      return [...this.opcoes].sort((a, b) =>
        this.normalizar(a.texto).localeCompare(this.normalizar(b.texto)),
      );
    },
    opcoesFiltradas() {
      if (!this.pesquisavel || !this.termoPesquisa) return this.opcoesOrdenadas;

      const termo = this.normalizar(this.termoPesquisa);
      return this.opcoesOrdenadas.filter(opcao =>
        this.normalizar(opcao.texto).includes(termo),
      );
    },
    opcoesExibidas() {
      if (this.multiplo) return this.opcoesFiltradas;
      return [this.opcaoDeAusenciaDeFiltro, ...this.opcoesFiltradas];
    },
    semResultados() {
      return (
        this.pesquisavel && !!this.termoPesquisa && this.opcoesFiltradas.length === 0
      );
    },
    rotuloExibido() {
      if (this.multiplo) {
        const quantidade = Array.isArray(this.value) ? this.value.length : 0;
        return quantidade > 0 && this.rotuloDeSelecionados
          ? this.rotuloDeSelecionados(quantidade)
          : this.rotuloSemSelecao;
      }

      const opcaoSelecionada = this.opcoesOrdenadas.find(
        opcao => opcao.valor === this.value,
      );
      return opcaoSelecionada ? opcaoSelecionada.texto : this.rotuloSemSelecao;
    },
    valorExibidoNoCampo() {
      return this.pesquisavel && this.aberto ? this.termoPesquisa : this.rotuloExibido;
    },
  },
  watch: {
    opcoesExibidas() {
      this.indiceEmFoco = -1;
    },
  },
  mounted() {
    document.addEventListener("mousedown", this.tratarCliqueFora);
  },
  beforeDestroy() {
    document.removeEventListener("mousedown", this.tratarCliqueFora);
  },
  methods: {
    normalizar(texto) {
      return (texto || "")
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "")
        .toLowerCase();
    },
    idDaOpcao(indice) {
      return `${this.idUnico}-opcao-${indice}`;
    },
    opcaoEstaSelecionada(opcao) {
      if (this.multiplo) return Array.isArray(this.value) && this.value.includes(opcao.valor);
      return this.value === opcao.valor;
    },
    abrir() {
      if (this.desabilitado || this.aberto) return;
      this.aberto = true;
      this.indiceEmFoco = -1;
    },
    fechar() {
      this.aberto = false;
      this.termoPesquisa = "";
      this.indiceEmFoco = -1;
    },
    aoPressionarNaCaixa(evento) {
      if (this.desabilitado) return;
      if (this.pesquisavel && evento.target === this.$refs.campo) return;

      evento.preventDefault();

      const deveAbrir = !this.aberto;
      this.$refs.campo.focus();

      if (deveAbrir) this.abrir();
      else this.fechar();
    },
    aoDigitar(evento) {
      if (!this.pesquisavel) return;
      this.termoPesquisa = evento.target.value;
    },
    selecionarOpcao(opcao) {
      if (this.desabilitado) return;

      if (this.multiplo) {
        const selecionados = Array.isArray(this.value) ? [...this.value] : [];
        const indice = selecionados.indexOf(opcao.valor);
        if (indice >= 0) selecionados.splice(indice, 1);
        else selecionados.push(opcao.valor);

        this.$emit("input", selecionados);
        this.$emit("selecionar", selecionados);
        return;
      }

      this.$emit("input", opcao.valor);
      this.$emit("selecionar", opcao.valor);
      this.fechar();
    },
    moverFoco(delta) {
      const total = this.opcoesExibidas.length;
      if (!total) return;
      this.indiceEmFoco = (this.indiceEmFoco + delta + total) % total;
    },
    tratarTeclado(evento) {
      if (this.desabilitado) return;

      switch (evento.key) {
        case "ArrowDown":
          evento.preventDefault();
          if (!this.aberto) this.abrir();
          else this.moverFoco(1);
          break;
        case "ArrowUp":
          evento.preventDefault();
          if (!this.aberto) this.abrir();
          else this.moverFoco(-1);
          break;
        case "Enter":
          if (this.aberto) {
            evento.preventDefault();
            const opcao = this.opcoesExibidas[this.indiceEmFoco];
            if (opcao) this.selecionarOpcao(opcao);
          }
          break;
        case "Escape":
          if (this.aberto) {
            evento.preventDefault();
            this.fechar();
          }
          break;
        case "Tab":
          if (this.aberto) this.fechar();
          break;
        default:
          break;
      }
    },
    tratarCliqueFora(evento) {
      if (this.aberto && !this.$el.contains(evento.target)) this.fechar();
    },
  },
};
</script>



// WEBPACK FOOTER //
// Combobox.vue?445e4965